import { DEFAULT_WPT_COLOR } from '../markers/MarkerOptions';
import {
    createLayeredPinIcon,
    createHoverOutlineIcon,
    createDirectionPinIcon,
    getLayeredPinRects,
} from '../markers/SelectedPinMarker';
import L from 'leaflet';
import { hexToRgba } from '../../util/ColorUtil';
import { updateMarkerZIndex } from '../layers/ExploreLayer';
import { DEFAULT_POI_COLOR, DEFAULT_POI_SHAPE } from '../../manager/PoiManager';
import { visibleMapRectPx } from '../layers/MapStateLayer';
import { SELECTED_MARKER_Z_INDEX } from './ZIndexes';

export const SELECTED_PIN_SIZE = 70;
export const SELECTED_ICON_SIZE = 36;
export const SELECTED_PIN_COLOR = '#FF8800';
export const EXPLORE_PHOTO_ICON_SIZE = 43;

const DIRECTION_PIN_TIP_MARGIN = 6;

export const toShape = (s) => (s === 'octagon' || s === 'hexagon' ? 'hexagon' : s);

const toColor = (c) => {
    if (!c) return DEFAULT_WPT_COLOR;
    if (c.startsWith('#')) return hexToRgba(c);
    return c;
};

function hideMarkerElement(el) {
    el.style.display = 'none';
    el.style.visibility = 'hidden';
    el.style.opacity = '0';
}

function showMarkerElement(el) {
    el.style.display = '';
    el.style.visibility = '';
    el.style.opacity = '';
}

function rectsIntersect(a, b) {
    return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

export function restoreHiddenMarkers(hiddenLayersRef) {
    hiddenLayersRef.current.forEach((layer) => {
        const el = layer.getElement();
        if (el) showMarkerElement(el);
    });
    hiddenLayersRef.current = [];
}

export function hideMarkersUnderPin(map, ctx) {
    const selectedPin = ctx.selectedCreatedLayerRef?.current;
    if (!selectedPin?.options.isSelection || !map.hasLayer(selectedPin)) {
        return;
    }

    restoreHiddenMarkers(ctx.selectedHiddenLayersRef);
    const pinRects = getLayeredPinRects(selectedPin.getElement());
    const hidden = [];

    // measure all markers before hiding any, so each hide does not force a new layout
    map.eachLayer((layer) => {
        if (!(layer instanceof L.Marker) || layer === selectedPin) {
            return;
        }
        const rect = layer.getElement()?.getBoundingClientRect();
        if (rect && pinRects.some((pinRect) => rectsIntersect(pinRect, rect))) {
            hidden.push(layer);
        }
    });

    hidden.forEach((layer) => hideMarkerElement(layer.getElement()));
    ctx.selectedHiddenLayersRef.current = hidden;
}

function buildSelectedIcon(marker, layerOptions = {}) {
    return createLayeredPinIcon({
        shape: toShape(marker.background ?? layerOptions.background),
        color: toColor(marker.color ?? layerOptions.color),
        iconHtml: marker.iconHtml ?? '',
        invertIcon: marker.invertIcon ?? false,
        size: SELECTED_PIN_SIZE,
        iconSize: SELECTED_ICON_SIZE,
    });
}

// Replaces the icon of an existing marker in-place (used for hover)
function applySelectedWithUpdateMarker(layer, marker) {
    if (!layer.options.originalIcon) {
        layer.options.originalIcon = layer.options.icon;
    }
    layer.setIcon(buildSelectedIcon(marker, layer.options));

    // After setIcon, custom 'add' handlers (e.g. SimpleDotMarker) moved the element into a lower-z-index pane (poiSecondaryPane).
    // Re-parent it into base markerPane (z-index 600) so it renders above all map markers.
    const el = layer.getElement();
    const map = layer._map;
    if (el && map) {
        const markerPane = map.getPane('markerPane');
        if (markerPane && el.parentNode !== markerPane) {
            markerPane.appendChild(el);
        }
    }
}

// Creates a new pin marker and adds it directly to the map (used for selection)
function applySelectedWithCreateMarker(map, latlng, marker, layerOptions = {}) {
    const latlngObj = latlng.lat !== undefined && latlng.lng !== undefined ? L.latLng(latlng.lat, latlng.lng) : latlng;
    const layer = L.marker(latlngObj, {
        icon: buildSelectedIcon(marker, layerOptions),
        interactive: false,
        zIndexOffset: SELECTED_MARKER_Z_INDEX,
    });
    layer.addTo(map);
    return layer;
}

// Restores the original icon of a marker that was updated in-place during hover
export function restoreOriginalIcon(layer) {
    const original = layer?.options?.originalIcon;
    if (original && layer.icon !== original) {
        layer.setIcon(original);
    }
}

function restoreUpdatedLayers(updatedLayersRef) {
    const current = updatedLayersRef?.current;
    const layers = Array.isArray(current) ? current : current ? [current] : [];
    layers.forEach(restoreOriginalIcon);
    if (updatedLayersRef) {
        updatedLayersRef.current = null;
    }
}

function clearHoverMarkers(ctx, map) {
    restoreUpdatedLayers(ctx.selectedUpdatedLayerRef);
    if (ctx.selectedCreatedLayerRef?.current && map.hasLayer(ctx.selectedCreatedLayerRef.current)) {
        map.removeLayer(ctx.selectedCreatedLayerRef.current);
        ctx.selectedCreatedLayerRef.current = null;
    }
}

function applyHoverUpdateMarker(map, layer, markerData) {
    if (!layer || !markerData || !map.hasLayer(layer)) {
        return null;
    }
    applySelectedWithUpdateMarker(layer, markerData);
    return layer;
}

// Shows an outline ring around a point hovered on the map
export function applyHoverOutline({ ctx, map, layer = null, latlng = null, shape, color, size }) {
    if (!ctx || !map) {
        return null;
    }

    resetSelectedPin({ ctx, map });

    const ll = latlng ?? layer?.getLatLng();
    if (!ll) {
        return null;
    }
    const latlngObj = ll.lat !== undefined && ll.lng !== undefined ? L.latLng(ll.lat, ll.lng) : ll;

    const outline = L.marker(latlngObj, {
        icon: createHoverOutlineIcon({
            shape: shape ?? DEFAULT_POI_SHAPE,
            color: toColor(color ?? DEFAULT_POI_COLOR),
            size,
        }),
        interactive: false,
        zIndexOffset: SELECTED_MARKER_Z_INDEX,
    });
    outline.addTo(map);
    ctx.selectedCreatedLayerRef.current = outline;

    return outline;
}

function rayRectEdge(cx, cy, ux, uy, left, top, right, bottom) {
    let t = Infinity;
    if (ux > 0) t = Math.min(t, (right - cx) / ux);
    else if (ux < 0) t = Math.min(t, (left - cx) / ux);
    if (uy > 0) t = Math.min(t, (bottom - cy) / uy);
    else if (uy < 0) t = Math.min(t, (top - cy) / uy);
    if (!Number.isFinite(t)) {
        t = 0;
    }

    return L.point(cx + ux * t, cy + uy * t);
}

export function applyDirectionPin({ ctx, map, latlng, markerData }) {
    if (!ctx || !map || !latlng || !markerData) {
        return null;
    }

    resetSelectedPin({ ctx, map });

    const rect = visibleMapRectPx(ctx, map);
    if (!rect) {
        return null;
    }
    const target = map.latLngToContainerPoint(L.latLng(latlng));
    let ux = target.x - rect.cx;
    let uy = target.y - rect.cy;
    if (ux === 0 && uy === 0) {
        uy = 1;
    }

    const m = DIRECTION_PIN_TIP_MARGIN;
    const tip = rayRectEdge(rect.cx, rect.cy, ux, uy, rect.left + m, rect.top + m, rect.right - m, rect.bottom - m);
    const angle = (Math.atan2(-ux, uy) * 180) / Math.PI;

    const pin = L.marker(map.containerPointToLatLng(tip), {
        icon: createDirectionPinIcon({
            color: toColor(markerData.color ?? DEFAULT_POI_COLOR),
            iconHtml: markerData.iconHtml,
            invertIcon: markerData.invertIcon,
            angle,
        }),
        interactive: false,
        zIndexOffset: SELECTED_MARKER_Z_INDEX,
    });
    pin.addTo(map);
    ctx.selectedCreatedLayerRef.current = pin;

    return pin;
}

// Removes the current selected/hover pin and restores hidden markers.
// The created pin is kept only if its idObj still matches the current selectedWpt id.
export function resetSelectedPin({ ctx, map, force = false }) {
    if (!ctx || !map) {
        return;
    }

    restoreHiddenMarkers(ctx.selectedHiddenLayersRef);

    const createdId = ctx.selectedCreatedLayerRef?.current?.options?.idObj;
    const wptId = ctx.selectedWpt?.id ?? null;
    if (!wptId || createdId !== wptId || force) {
        if (ctx.selectedCreatedLayerRef?.current && map.hasLayer(ctx.selectedCreatedLayerRef.current)) {
            map.removeLayer(ctx.selectedCreatedLayerRef.current);
            ctx.selectedCreatedLayerRef.current = null;
        }
    }

    restoreUpdatedLayers(ctx.selectedUpdatedLayerRef);
}

export function applySelectedPins({ ctx, map, items }) {
    if (!ctx || !map || !items?.length) {
        return [];
    }

    clearHoverMarkers(ctx, map);

    const selectedLayers = items
        .map(({ layer, markerData }) => applyHoverUpdateMarker(map, layer, markerData))
        .filter(Boolean);

    if (!selectedLayers.length) {
        ctx.selectedUpdatedLayerRef.current = null;
        return [];
    }

    ctx.selectedUpdatedLayerRef.current = selectedLayers;
    updateMarkerZIndex(new L.FeatureGroup(selectedLayers), SELECTED_MARKER_Z_INDEX);

    return selectedLayers;
}

// Main entry point for showing a selected or hovered pin.
// isSelection=true: creates a new separate pin on top of the map and hides the markers it overlaps.
// isSelection=false (hover): updates the existing marker icon in-place.
export function applySelectedPin({ ctx, map, layer = null, latlng = null, markerData, isSelection = false }) {
    if (!ctx || !map || !markerData) {
        return null;
    }

    const ll = latlng ?? layer?.getLatLng();
    if (!ll) return null;

    if (isSelection) {
        resetSelectedPin({ ctx, map, force: true });
        ctx.selectedHiddenLayersRef.current = [];
    } else {
        clearHoverMarkers(ctx, map);
    }

    let selectedLayer;

    if (isSelection) {
        selectedLayer = applySelectedWithCreateMarker(map, ll, markerData, layer?.options);
        selectedLayer.options.idObj = layer?.options?.idObj ?? null;
        selectedLayer.options.isSelection = true;
        ctx.selectedCreatedLayerRef.current = selectedLayer;
        const markerColor = String(markerData.color ?? '').replace(/^#/, '');
        selectedLayer.getElement().setAttribute('id', `se-selected-marker-${layer?.options?.name}-${markerColor}`);
        hideMarkersUnderPin(map, ctx);
    } else if (layer && map.hasLayer(layer)) {
        selectedLayer = applyHoverUpdateMarker(map, layer, markerData);
        ctx.selectedUpdatedLayerRef.current = selectedLayer;
    } else {
        // Fallback: layer is not on the map — create a new pin at the given latlng.
        selectedLayer = applySelectedWithCreateMarker(map, ll, markerData, layer?.options);
        ctx.selectedCreatedLayerRef.current = selectedLayer;
    }
    updateMarkerZIndex(new L.FeatureGroup([selectedLayer]), SELECTED_MARKER_Z_INDEX);

    return selectedLayer;
}
